/**
 * useConflictResolution Hook
 *
 * Handles conflict detection and resolution for booking operations.
 * Detects overbooking and project/customer changes, allows selective resolution.
 */

import type { IBooking, IPostBooking } from "@/services/types";
import { sumHours } from "@/utils/booking";
import {
  formatDisplayDate,
  formatHours as formatHoursUtil,
} from "@/utils/date";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useBookingMutations } from "./useBookingMutations";

/**
 * Conflict info for a single date
 */
export interface ConflictInfo {
  index: number;
  date: Date;
  existingBookings: IBooking[];
  postBooking: IPostBooking;
  projectHoursFrom: number;
  projectHoursTo: number;
  totalHoursFrom: number;
  totalHoursTo: number;
  hoursDelta: number;
  isIssue: boolean;
  isOverbooked: boolean;
  isProjectChange: boolean;
}

/**
 * Conflict resolution state
 */
export interface ConflictResolutionState {
  isActive: boolean;
  isLoading: boolean;
  postBookings: IPostBooking[];
  conflicts: IBooking[];
  omittedIndices: Set<number>;
  ignoredBookingIds: number[];
  autoResolve: boolean;
  error: Error | null;
}

/**
 * Resolution result
 */
export interface ResolutionResult {
  resolved: IPostBooking[];
  skipped: IPostBooking[];
}

const DEFAULT_HOURS_IN_DAY = 8;

export function useConflictResolution(hoursInDay = DEFAULT_HOURS_IN_DAY) {
  const { checkConflicts } = useBookingMutations();

  // State
  const [state, setState] = useState<ConflictResolutionState>({
    isActive: false,
    isLoading: false,
    postBookings: [],
    conflicts: [],
    omittedIndices: new Set(),
    ignoredBookingIds: [],
    autoResolve: true,
    error: null,
  });

  // ============ DERIVED STATE ============

  /**
   * Process conflicts into detailed info
   */
  const conflictInfos = useMemo((): ConflictInfo[] => {
    return state.postBookings.map((post, index) => {
      const existingBookings = state.conflicts ?? [];

      // Calculate hours from existing bookings on same project/employee/activity
      const sameProjectBookings = existingBookings.filter(
        (b) =>
          b.project?.id === post.project && b.employee?.id === post.employee,
      );
      const projectHoursFrom = sumHours(sameProjectBookings);

      // Calculate total hours from all existing bookings
      const totalHoursFrom = sumHours(existingBookings);

      // Calculate hours from ignored bookings (being edited)
      const ignoredBookings = existingBookings.filter((b) =>
        state.ignoredBookingIds.includes(b.id),
      );
      const ignoredHours = sumHours(ignoredBookings);

      // Calculate new totals
      const projectHoursTo = post.hours;
      const totalHoursTo = totalHoursFrom + post.hours - ignoredHours;
      const hoursDelta = totalHoursTo - totalHoursFrom;

      // Determine if this is an issue
      const wasOverbooked = totalHoursFrom > hoursInDay;
      const willBeOverbooked = totalHoursTo > hoursInDay;
      const isOverbooked = willBeOverbooked && !wasOverbooked;

      // Check if project hours are changing (conflict only if editing different hours)
      const isProjectChange =
        projectHoursFrom !== 0 &&
        projectHoursFrom !== projectHoursTo &&
        ignoredHours !== projectHoursFrom;

      const isIssue =
        existingBookings.length > 0 && (isOverbooked || isProjectChange);

      return {
        index,
        date: post.date instanceof Date ? post.date : new Date(post.date),
        existingBookings,
        postBooking: post,
        projectHoursFrom,
        projectHoursTo,
        totalHoursFrom,
        totalHoursTo,
        hoursDelta,
        isIssue,
        isOverbooked,
        isProjectChange,
      };
    });
  }, [
    state.postBookings,
    state.conflicts,
    state.ignoredBookingIds,
    hoursInDay,
  ]);

  /**
   * Filter to only issues
   */
  const issues = useMemo(() => {
    return conflictInfos.filter((c) => c.isIssue);
  }, [conflictInfos]);

  /**
   * Count of conflict dates
   */
  const totalConflictDates = issues.length;

  /**
   * Check if any conflicts exist
   */
  const hasConflicts = totalConflictDates > 0;

  /**
   * Check if all posts are omitted
   */
  const allOmitted = state.omittedIndices.size === state.postBookings.length;

  /**
   * Get posts that will be saved (not omitted)
   */
  const resolvedPosts = useMemo(() => {
    return state.postBookings.filter(
      (_, idx) => !state.omittedIndices.has(idx),
    );
  }, [state.postBookings, state.omittedIndices]);

  /**
   * Get posts that are omitted
   */
  const skippedPosts = useMemo(() => {
    return state.postBookings.filter((_, idx) => state.omittedIndices.has(idx));
  }, [state.postBookings, state.omittedIndices]);

  // ============ ACTIONS ============

  /**
   * Start conflict resolution process
   */
  const startResolution = useCallback(
    async (
      postBookings: IPostBooking[],
      options: {
        ignoredBookingIds?: number[];
        autoResolve?: boolean;
      } = {},
    ) => {
      setState((prev) => ({
        ...prev,
        isActive: true,
        isLoading: true,
        postBookings,
        conflicts: [],
        omittedIndices: new Set(),
        ignoredBookingIds: options.ignoredBookingIds ?? [],
        autoResolve: options.autoResolve ?? true,
        error: null,
      }));

      try {
        const conflicts = await checkConflicts(postBookings);

        setState((prev) => ({
          ...prev,
          conflicts,
          isLoading: false,
        }));
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error:
            error instanceof Error
              ? error
              : new Error("Failed to check conflicts"),
        }));
      }
    },
    [checkConflicts],
  );

  /**
   * Cancel resolution and close
   */
  const cancelResolution = useCallback(() => {
    setState({
      isActive: false,
      isLoading: false,
      postBookings: [],
      conflicts: [],
      omittedIndices: new Set(),
      ignoredBookingIds: [],
      autoResolve: true,
      error: null,
    });
  }, []);

  /**
   * Toggle omission of a post
   */
  const toggleOmit = useCallback((index: number) => {
    setState((prev) => {
      const newOmitted = new Set(prev.omittedIndices);
      if (newOmitted.has(index)) {
        newOmitted.delete(index);
      } else {
        newOmitted.add(index);
      }
      return { ...prev, omittedIndices: newOmitted };
    });
  }, []);

  /**
   * Check if index is omitted
   */
  const isOmitted = useCallback(
    (index: number) => {
      return state.omittedIndices.has(index);
    },
    [state.omittedIndices],
  );

  /**
   * Set all to save (clear omissions)
   */
  const saveAll = useCallback(() => {
    setState((prev) => ({ ...prev, omittedIndices: new Set() }));
  }, []);

  /**
   * Set all issues to omit
   */
  const omitAll = useCallback(() => {
    const issueIndices = new Set(issues.map((i) => i.index));
    setState((prev) => ({ ...prev, omittedIndices: issueIndices }));
  }, [issues]);

  /**
   * Get resolution result
   */
  const getResolution = useCallback((): ResolutionResult => {
    return {
      resolved: resolvedPosts,
      skipped: skippedPosts,
    };
  }, [resolvedPosts, skippedPosts]);

  /**
   * Complete resolution and return posts to save
   */
  const completeResolution = useCallback((): IPostBooking[] => {
    const posts = resolvedPosts;
    cancelResolution();
    return posts;
  }, [resolvedPosts, cancelResolution]);

  // ============ AUTO-RESOLVE ============

  /**
   * Auto-resolve when no conflicts and autoResolve is enabled
   */
  useEffect(() => {
    if (
      state.isActive &&
      !state.isLoading &&
      state.autoResolve &&
      !hasConflicts &&
      state.postBookings.length > 0
    ) {
      // No conflicts - auto-resolve is handled by the component using this hook
      // We just provide the signal via hasConflicts === false
    }
  }, [
    state.isActive,
    state.isLoading,
    state.autoResolve,
    hasConflicts,
    state.postBookings.length,
  ]);

  // ============ FORMAT HELPERS ============

  /**
   * Format hours for display
   */
  const formatHours = useCallback((hours: number): string => {
    return formatHoursUtil(hours);
  }, []);

  /**
   * Format date for display
   */
  const formatConflictDate = useCallback((date: Date): string => {
    return formatDisplayDate(date);
  }, []);

  return {
    // State
    isActive: state.isActive,
    isLoading: state.isLoading,
    postBookings: state.postBookings,
    conflicts: state.conflicts,
    omittedIndices: state.omittedIndices,
    error: state.error,

    // Computed
    conflictInfos,
    issues,
    totalConflictDates,
    hasConflicts,
    allOmitted,
    resolvedPosts,
    skippedPosts,

    // Actions
    startResolution,
    cancelResolution,
    toggleOmit,
    isOmitted,
    saveAll,
    omitAll,
    getResolution,
    completeResolution,

    // Helpers
    formatHours,
    formatConflictDate,
  };
}
